// Chemin: C:\gestion-copro\src\services\conseilSyndical.service.ts

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { 
  MembreCS, 
  MembreCSCreateData, 
  MembreCSUpdateData, 
  MembreCSResume,
  ROLE_CS_ORDRE 
} from '../types/conseilSyndical.types';

const COLLECTION_NAME = 'membresCS';

/**
 * Récupère tous les membres CS d'une copropriété
 */
export const getMembresCSByCopropriete = async (
  coproprieteId: string,
  actifsUniquement: boolean = true
): Promise<MembreCS[]> => {
  let q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId)
  );
  
  if (actifsUniquement) {
    q = query(q, where('actif', '==', true));
  }
  
  const snapshot = await getDocs(q);
  const membres = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as MembreCS));
  
  // Trier par rôle (président en premier)
  return membres.sort((a, b) => ROLE_CS_ORDRE[a.role] - ROLE_CS_ORDRE[b.role]);
};

/**
 * Récupère un membre CS par son ID
 */
export const getMembreCSById = async (membreId: string): Promise<MembreCS | null> => {
  const docRef = doc(db, COLLECTION_NAME, membreId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return {
    id: docSnap.id,
    ...docSnap.data()
  } as MembreCS;
};

/**
 * Crée un nouveau membre CS
 */
export const createMembreCS = async (data: MembreCSCreateData): Promise<string> => {
  const now = Timestamp.now();
  
  // Construire l'objet sans champs undefined
  const membreData: Record<string, any> = {
    coproprieteId: data.coproprieteId,
    nom: data.nom,
    prenom: data.prenom,
    role: data.role,
    dateElection: Timestamp.fromDate(data.dateElection),
    actif: data.actif !== undefined ? data.actif : true,
    createdAt: now,
    updatedAt: now
  };
  
  // Champs optionnels
  if (data.email) membreData.email = data.email;
  if (data.telephone) membreData.telephone = data.telephone;
  if (data.lotId) membreData.lotId = data.lotId;
  if (data.lotNumero) membreData.lotNumero = data.lotNumero;
  if (data.userId) membreData.userId = data.userId;
  if (data.agElectionRef) membreData.agElectionRef = data.agElectionRef;
  if (data.dateFinMandat) {
    membreData.dateFinMandat = Timestamp.fromDate(data.dateFinMandat);
  }
  if (data.notes) membreData.notes = data.notes;
  
  const docRef = await addDoc(collection(db, COLLECTION_NAME), membreData);
  return docRef.id;
};

/**
 * Met à jour un membre CS existant
 */
export const updateMembreCS = async (
  membreId: string, 
  data: MembreCSUpdateData
): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, membreId);
  
  const updateData: Record<string, any> = {
    updatedAt: Timestamp.now()
  };
  
  // Champs simples
  if (data.nom !== undefined) updateData.nom = data.nom;
  if (data.prenom !== undefined) updateData.prenom = data.prenom;
  if (data.email !== undefined) updateData.email = data.email;
  if (data.telephone !== undefined) updateData.telephone = data.telephone;
  if (data.lotId !== undefined) updateData.lotId = data.lotId;
  if (data.lotNumero !== undefined) updateData.lotNumero = data.lotNumero;
  if (data.userId !== undefined) updateData.userId = data.userId;
  if (data.role !== undefined) updateData.role = data.role;
  if (data.agElectionRef !== undefined) updateData.agElectionRef = data.agElectionRef;
  if (data.actif !== undefined) updateData.actif = data.actif;
  if (data.notes !== undefined) updateData.notes = data.notes;
  
  // Dates
  if (data.dateElection !== undefined) {
    updateData.dateElection = Timestamp.fromDate(data.dateElection);
  }
  if (data.dateFinMandat !== undefined) {
    updateData.dateFinMandat = Timestamp.fromDate(data.dateFinMandat);
  }
  
  await updateDoc(docRef, updateData);
};

/**
 * Désactive un membre CS (fin de mandat)
 */
export const desactiverMembreCS = async (
  membreId: string,
  dateFinMandat?: Date
): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, membreId);
  
  const updateData: Record<string, any> = {
    actif: false,
    updatedAt: Timestamp.now()
  };
  
  if (dateFinMandat) {
    updateData.dateFinMandat = Timestamp.fromDate(dateFinMandat);
  }
  
  await updateDoc(docRef, updateData);
};

/**
 * Supprime un membre CS
 */
export const deleteMembreCS = async (membreId: string): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, membreId);
  await deleteDoc(docRef);
};

/**
 * Récupère les membres CS résumés (pour les listes)
 */
export const getMembresCSResume = async (
  coproprieteId: string
): Promise<MembreCSResume[]> => {
  const membres = await getMembresCSByCopropriete(coproprieteId, false);
  
  return membres.map(membre => ({
    id: membre.id,
    nomComplet: `${membre.prenom} ${membre.nom}`,
    role: membre.role,
    lotNumero: membre.lotNumero,
    actif: membre.actif
  }));
};

/**
 * Récupère le président du CS
 */
export const getPresidentCS = async (
  coproprieteId: string
): Promise<MembreCS | null> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    where('role', '==', 'president'),
    where('actif', '==', true)
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data()
  } as MembreCS;
};

/**
 * Compte les membres actifs du CS
 */
export const countMembresCSActifs = async (
  coproprieteId: string
): Promise<number> => {
  const membres = await getMembresCSByCopropriete(coproprieteId, true);
  return membres.length;
};